import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { RouteConfig } from "@/routes/routes.types";
import ICONS from "@/constant/IconManager";
import StatTesting from "@/components/projectCommon/StatTesting/StatTesting";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { dequal } from "dequal";
import {
  BUTTERLFY,
  DIFFERENTIATION,
  LINE,
  MARKETS,
  PERCEPTUAL,
  POINT_IN_TIME,
} from "@/constant/constantsThrougoutProject";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";

interface HeaderProps {
  routes: RouteConfig[];
}

const Header = ({ routes }: HeaderProps) => {
  const location = useLocation();
  const { period, mode, visualizationType } = useSelector(
    (state: RootState) => ({
      mode: state.userSelection.mode,
      period: state.userSelection.period,
      visualizationType: state.userSelection.visualizationType,
    }),
    (prev, next) => dequal(prev, next),
  );

  const attributeLabels = useSelector(
    (state: RootState) => {
      if (
        ROUTES_CHILDREN_LOOKUP[location.pathname]?.multipleOptionsForKeys?.[
          POINT_IN_TIME
        ]?.includes(MARKETS)
      ) {
        return state.activeGroup.attributeLabels["global"] || {};
      } else {
        if (state.userSelection.markets?.length > 0) {
          const currentMarket = state.userSelection.markets[0];
          if (state.activeGroup.attributeLabels[currentMarket]) {
            return state.activeGroup.attributeLabels[currentMarket];
          }
          return state.activeGroup.attributeLabels["default"] || {};
        }
      }
      return state.activeGroup.attributeLabels["default"] || {};
    },
    (prev, next) => dequal(prev, next),
  );

  const activeRoute = useMemo(() => {
    const result = routes.find((route) =>
      location.pathname?.startsWith(`/${route.id}`),
    );
    if (result) {
      return result;
    }
    return null;
  }, [location.pathname]);

  const title =
    attributeLabels?.[`${activeRoute?.key}`]?.label ?? activeRoute?.name;

  return (
    <div className="relative h-[50px] flex items-center justify-between py-2 pl-[9px] pr-[12px] px-[3px] border-b border-[#E0E6F0] bg-white">
      <div>{/* <GroupSelect /> */}</div>
      <div className="absolute left-[50%] transform -translate-x-1/2">
        {activeRoute ? (
          <div className="flex gap-[10px] tracking-[0.5px] items-center font-bold text-medium">
            {activeRoute.iconKey ? (
              <img
                src={ICONS[activeRoute.iconKey]?.dark}
                alt={activeRoute.name}
                className="h-[19px] w-[19px]"
              />
            ) : (
              ""
            )}
            <span>{title}</span>
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        {!location.pathname?.startsWith("/reports") &&
          !(
            ["MV_1", "MV_2", "MV_4", "MV_8", "MV_12"]?.includes(period) &&
            mode === "TREND"
          ) &&
          visualizationType !== BUTTERLFY &&
          visualizationType !== PERCEPTUAL &&
          visualizationType !== DIFFERENTIATION && <StatTesting />}
      </div>
    </div>
  );
};

export default Header;
